import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Bell, BellOff, CheckCircle, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface NotificationPermission {
  granted: boolean;
  denied: boolean;
  default: boolean;
}

export const PushNotification: React.FC = () => {
  const [permission, setPermission] = useState<NotificationPermission>({
    granted: false,
    denied: false,
    default: false,
  });
  const [isSupported, setIsSupported] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Vérifier si les notifications sont supportées
    if ('Notification' in window) {
      setIsSupported(true);
      setPermission({
        granted: Notification.permission === 'granted',
        denied: Notification.permission === 'denied',
        default: Notification.permission === 'default',
      });
    }
  }, []);

  const requestPermission = async () => {
    try {
      const result = await Notification.requestPermission();
      setPermission({
        granted: result === 'granted',
        denied: result === 'denied',
        default: result === 'default',
      });

      if (result === 'granted') {
        toast({
          title: "✅ Notifications activées",
          description: "Vous recevrez maintenant des notifications pour vos sessions de travail",
        });
      } else {
        toast({
          title: "⚠️ Notifications refusées",
          description: "Vous pouvez activer les notifications dans les paramètres de votre navigateur",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Erreur lors de la demande de permission:', error);
      toast({
        title: "❌ Erreur",
        description: "Impossible d'activer les notifications",
        variant: "destructive",
      });
    }
  };

  const sendTestNotification = () => {
    if (permission.granted) {
      new Notification('Time Tracker', {
        body: 'Ceci est une notification de test pour vérifier que tout fonctionne correctement.',
        icon: '/pwa-icon.svg',
        badge: '/pwa-icon.svg',
        tag: 'test-notification',
      });
    }
  };

  const sendSessionNotification = (title: string, body: string) => {
    if (permission.granted) {
      new Notification(title, {
        body,
        icon: '/pwa-icon.svg',
        badge: '/pwa-icon.svg',
        tag: 'session-notification',
        requireInteraction: true,
        actions: [
          {
            action: 'view',
            title: 'Voir',
          },
          {
            action: 'dismiss',
            title: 'Fermer',
          },
        ],
      });
    }
  };

  if (!isSupported) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BellOff className="h-5 w-5 text-gray-500" />
            Notifications non supportées
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600">
            Votre navigateur ne supporte pas les notifications push.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Notifications Push
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          {permission.granted ? (
            <CheckCircle className="h-5 w-5 text-green-600" />
          ) : permission.denied ? (
            <AlertCircle className="h-5 w-5 text-red-600" />
          ) : (
            <Bell className="h-5 w-5 text-yellow-600" />
          )}
          <span className="text-sm">
            {permission.granted
              ? 'Notifications activées'
              : permission.denied
              ? 'Notifications refusées'
              : 'Notifications non configurées'}
          </span>
        </div>

        {permission.default && (
          <Button onClick={requestPermission} className="w-full">
            <Bell className="h-4 w-4 mr-2" />
            Activer les notifications
          </Button>
        )}

        {permission.granted && (
          <div className="space-y-2">
            <Button onClick={sendTestNotification} variant="outline" className="w-full">
              Envoyer une notification de test
            </Button>
            <p className="text-xs text-gray-500">
              Les notifications vous informeront des événements importants comme :
            </p>
            <ul className="text-xs text-gray-500 list-disc list-inside space-y-1">
              <li>Début/fin de session de travail</li>
              <li>Rappels de pause</li>
              <li>Résumé quotidien</li>
              <li>Alertes de dépassement d'heures</li>
            </ul>
          </div>
        )}

        {permission.denied && (
          <div className="bg-yellow-50 p-3 rounded-lg">
            <p className="text-sm text-yellow-800">
              Pour activer les notifications, allez dans les paramètres de votre navigateur
              et autorisez les notifications pour ce site.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Hook pour utiliser les notifications dans d'autres composants
export const useNotifications = () => {
  const sendNotification = (title: string, body: string) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, {
        body,
        icon: '/pwa-icon.svg',
        badge: '/pwa-icon.svg',
        tag: 'time-tracker-notification',
      });
    }
  };

  return { sendNotification };
};

export default PushNotification; 